import { useEffect, useState } from "react";
import { Loader2, Moon, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { mountainToViewer } from "../scheduled-jobs/jobMeta";
import { When } from "../scheduled-jobs/When";

const err = (e, d) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || d);
const pick = (q) => ({ enabled: q.enabled, start: q.start, end: q.end, weekends: q.weekends });
const viewer = (hm) => { const [h, m] = hm.split(":").map(Number); return mountainToViewer(h, m); };

function HeldList({ onReleased, canEdit }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin-alerts/held").then(({ data }) => setItems(data.items)).catch(() => setItems([])); }, []);
  const release = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin-alerts/held/release"); toast.success(`Sent ${data.alerts ?? 0} held alert(s)${data.dropped ? `, ${data.dropped} left out (fixed overnight)` : ""}`); onReleased(); }
    catch (e) { err(e, "Could not send the held alerts"); } finally { setBusy(false); }
  };
  if (!items) return <Loader2 className="h-4 w-4 animate-spin text-slate-300" />;
  return (
    <div className="space-y-2 rounded-lg border border-indigo-100 bg-white p-3" data-testid="quiet-held-list">
      {items.map((i, n) => (
        <div key={n} className="flex items-start gap-3 text-xs" data-testid={`quiet-held-item-${n}`}>
          <When iso={i.created_at} className="w-32 shrink-0 text-slate-500" />
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-wide text-slate-400">{i.label}</div>
            <div className={i.kind === "recovered" ? "text-emerald-700" : i.kind === "failed" ? "text-rose-700" : "text-slate-700"}>{i.subject}</div></div>
        </div>))}
      {!items.length && <p className="text-xs text-slate-500">Nothing is held.</p>}
      {canEdit && items.length > 0 && <Button size="sm" variant="outline" className="h-7" onClick={release} disabled={busy} data-testid="quiet-release-now">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send them now</Button>}
    </div>
  );
}

/** Quiet hours: when "Hold overnight" alerts wait, and what is waiting right now. */
export function QuietHoursPanel({ quiet, canEdit, onSaved }) {
  const [f, setF] = useState(pick(quiet));
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const dirty = JSON.stringify(f) !== JSON.stringify(pick(quiet));
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin-alerts/quiet-hours", f); toast.success("Quiet hours saved"); onSaved(); }
    catch (e) { err(e, "Could not save quiet hours"); } finally { setBusy(false); }
  };
  const vs = viewer(f.start), ve = viewer(f.end);
  return (
    <div className="space-y-3 border-b border-slate-100 bg-indigo-50/40 px-5 py-4" data-testid="quiet-hours-panel">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-800"><Moon className="h-4 w-4 text-indigo-500" /> Quiet hours
          <Switch checked={f.enabled} disabled={!canEdit} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="quiet-enabled" /></label>
        <div className="flex items-center gap-1.5 text-xs text-slate-600">From
          <Input type="time" value={f.start} disabled={!canEdit || !f.enabled} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-8 w-28 text-xs" data-testid="quiet-start" />
          to <Input type="time" value={f.end} disabled={!canEdit || !f.enabled} onChange={(e) => setF({ ...f, end: e.target.value })} className="h-8 w-28 text-xs" data-testid="quiet-end" /> MT</div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={f.weekends} disabled={!canEdit || !f.enabled} onCheckedChange={(v) => setF({ ...f, weekends: v })} data-testid="quiet-weekends" />
          All weekend too (Friday night → Monday morning)</label>
        {canEdit && dirty && <Button size="sm" className="h-8" onClick={save} disabled={busy} data-testid="quiet-save">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save</Button>}
      </div>
      {f.enabled && vs && ve && <p className="text-[11px] text-slate-500" data-testid="quiet-viewer-times">That's {vs} to {ve} your time.</p>}
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="quiet-status">
        {!quiet.enabled ? <span>Off — every alert is sent straight away.</span>
          : quiet.quiet_until ? <span className="font-medium text-indigo-800">Quiet now — held alerts go out at <When iso={quiet.quiet_until} className="inline" />.</span>
            : <span>Not quiet right now{quiet.next_start && <> — next quiet period starts <When iso={quiet.next_start} className="inline" /></>}.</span>}
        <button type="button" className="font-medium text-indigo-700 hover:underline disabled:text-slate-400 disabled:no-underline" disabled={!quiet.held_total}
                onClick={() => setOpen(!open)} data-testid="quiet-held-toggle">{quiet.held_total} held{quiet.held_total ? (open ? " · hide" : " · show") : ""}</button>
      </div>
      {open && quiet.held_total > 0 && <HeldList canEdit={canEdit} onReleased={() => { setOpen(false); onSaved(); }} />}
      <p className="text-[11px] text-slate-500">Alerts with <b>Hold overnight</b> on wait during these hours. Each person then gets one e-mail with everything held for them; a failure that fixed itself before morning is left out.</p>
    </div>
  );
}
